import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FloatingBottomNav = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScreenTooNarrow, setIsScreenTooNarrow] = useState(false);

  // Same menu structure as the main navbar
  const menuItems = {
    about: {
      label: "HOME",
      items: [
        { path: "/", label: "ABOUT US" },
        { path: "/#vision", label: "VISION" },
        { path: "/#team", label: "TEAM" },
        { path: "/#our-story", label: "OUR STORY" },
        { path: "/#contact", label: "CONTACT" },
        { path: "/#what-we-do", label: "WHAT WE DO" },
      ],
    },
    volunteer: {
      label: "VOLUNTEER",
      items: [
        { path: "/volunteer/calendar", label: "CALENDAR" },
        { path: "/volunteer/branch-creation", label: "BRANCH CREATION" },
        { path: "/volunteer/branch-renewal", label: "BRANCH RENEWAL" },
      ],
    },
    publications: {
      label: "PUBLICATIONS",
      items: [
        { path: "/publications/sfr-reflections", label: "SFR REFLECTIONS" },
      ]
    },
    gallery: {
      label: "GALLERY",
      items: [
        { path: "/gallery/events", label: "EVENT GALLERY" },
        { path: "/gallery/newsletters", label: "NEWSLETTERS GALLERY" },
      ],
    },
    // resources: {
    //   label: "RESOURCES",
    //   items: [
    //     { path: "/resources/general", label: "GENERAL WEBSITES" },
    //     { path: "/resources/africa", label: "AFRICAN REFUGEE CRISIS" },
    //     { path: "/resources/middleeast", label: "MIDDLE EASTERN REFUGEE CRISIS" },
    //     { path: "/resources/policy", label: "REFUGEE MIGRATION POLICY" },
    //     { path: "/resources/latinamerica", label: "LATIN AMERICA REFUGEE CRISIS" },
    //   ],
    // },
  };

  const standardMenuItems = [
    // { path: "/", label: "HOME" },
    { path: "/impact", label: "IMPACT" },
    { path: "/merch", label: "MERCH" }
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      // Get footer element
      const footer = document.querySelector('footer');
      
      if (!footer) {
        // If no footer is found, just show when scrolled down past 200px
        setIsVisible(window.scrollY > 200 && !isScreenTooNarrow);
        return;
      }
      
      // Calculate position of footer and viewport
      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Hide the nav when footer is visible in the viewport
      // We add a small buffer (100px) to hide the nav before the footer is fully in view
      const isFooterVisible = footerRect.top < windowHeight + 100;
      
      // Only show when:
      // 1. Scrolled down past 200px
      // 2. Not at the footer yet
      // 3. Screen is not too narrow
      setIsVisible(window.scrollY > 200 && !isFooterVisible && !isScreenTooNarrow);
    };

    const handleResize = () => {
      // Set a breakpoint for narrow screens (e.g., 640px)
      setIsScreenTooNarrow(window.innerWidth < 640);
      handleScroll(); // Call handleScroll to update visibility
    };

    // Call once to set initial state
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isScreenTooNarrow]);

  // If not on homepage, let default navigation happen (browser will navigate to /)
  // Then use onload or useEffect to scroll to the section after page load
  const handleLinkClick = (e, path) => {
    // Special case for home button
    if (path === "/") {
      const isHomepage = window.location.pathname === "/" || window.location.pathname === "";
      if (isHomepage) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Otherwise let default navigation happen
      return;
    }
    
    // Existing code for section navigation
    if (path.includes("/#")) {
      const isHomepage = window.location.pathname === "/" || window.location.pathname === "";
      
      if (isHomepage) {
        e.preventDefault();
        const sectionId = path.split("#")[1];
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          setActiveDropdown(null);
        }
      } else {
        const sectionId = path.split("#")[1];
        sessionStorage.setItem("scrollToSection", sectionId);
      }
    }
  };

  // Add this useEffect in your Homepage component:
  useEffect(() => {
    // Check if we need to scroll to a section
    const scrollToSection = sessionStorage.getItem("scrollToSection");
    if (scrollToSection) {
      // Clear it immediately to prevent future unwanted scrolls
      sessionStorage.removeItem("scrollToSection");

      // Slight delay to ensure all content is rendered
      setTimeout(() => {
        const element = document.getElementById(scrollToSection);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const NavItem = ({ children, className = "" }) => (
    <div className={`group relative ${className}`}>
      <div className="relative">
        {children}
        <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full"></div>
      </div>
    </div>
  );

  const getDropdownAlignment = (key) => {
    return "bottom-full right-0";
  };

  // Dropdown menu component with onclick handler that closes the dropdown
  const DropdownMenu = ({ items, type }) => (
    <motion.ul 
      className={`absolute ${getDropdownAlignment(type)} z-50 w-56 bg-background shadow-lg rounded-lg`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
      style={{ maxHeight: '40vh', overflowY: 'auto' }}
      onClick={() => setActiveDropdown(null)}
    >
      {items.map((item) => (
        <li key={item.path} className="w-full">
          <NavItem className="w-full">
            <a 
              href={item.path} 
              className="block w-full px-3 py-2 text-xs text-secondary text-left transition-colors hover:bg-lightTeal"
              onClick={(e) => {
                handleLinkClick(e, item.path);
                setActiveDropdown(null);
              }}
            >
              {item.label}
            </a>
          </NavItem>
        </li>
      ))}
    </motion.ul>
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-6 left-0 right-0 flex justify-center z-50 sm:flex hidden"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <nav className="bg-white rounded-full shadow-lg border border-lightTeal px-4 py-2">
            <div className="flex items-center justify-center space-x-3">
              {/* Brand mark - shortened version */}
              <button onClick={(e) => handleLinkClick(e, "/")} className="text-primary font-display text-xs font-bold mr-1">SFR</button>
              
              {/* Standard items */}
              {standardMenuItems.map((item) => (
                <NavItem key={item.path}>
                  <a href={item.path} onClick={(e) => handleLinkClick(e, item.path)} className="px-2 font-body text-xs text-secondary">
                    {item.label}
                  </a>
                </NavItem>
              ))}
              
              {/* Dropdown items - changed from onMouseEnter/onMouseLeave to onClick */}
              {Object.entries(menuItems).map(([key, { label, items }]) => (
                <div
                  key={key}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(key)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <NavItem>
                    <span className="cursor-pointer px-2 font-body text-xs text-secondary">
                      {label}
                    </span>
                  </NavItem>
                  {activeDropdown === key && (
                    <DropdownMenu items={items} type={key} />
                  )}
                </div>
              ))}
              
              {/* Donate button */}
              <a
                // href="/#donation"
                href="/donate"
                className="rounded-full bg-cta px-3 py-1 font-body text-xs text-white transition-colors hover:bg-opacity-90"
                // onClick={(e) => handleLinkClick(e, '/#donation')}
                onClick={(e) => handleLinkClick(e, '/donate')}
              >
                DONATE
              </a>
            </div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingBottomNav;